/**
 * This module provides a toggle component for managing a location's transient status.
 * It presents a user-friendly interface for switching between transient and non-transient
 * states, with appropriate visual feedback and help text.
 */
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
  toggleButtonClasses,
  toggleButtonGroupClasses,
} from '@mui/material';

/**
 * Styled version of Material-UI's ToggleButtonGroup.
 * Customizes the appearance of toggle buttons to match Hardfin's design:
 * - Removes default borders
 * - Adds consistent spacing
 * - Uses rounded corners
 * - Maintains readable text
 */
const TransientButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonClasses.root}.${toggleButtonGroupClasses.grouped}`]: {
    border: 0,
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
  },
}));

interface Props {
  /**
   * Current transient status of the location.
   * - true: Location is transient
   * - false: Location is non-transient
   */
  isTransient: boolean;

  /**
   * Callback fired when the transient status changes.
   * @param isTransient - The new transient status
   */
  onChange: (isTransient: boolean) => void;
}

/**
 * A toggle component for controlling a location's transient status.
 * Provides a simple Yes/No interface with an information tooltip explaining
 * the implications of the setting.
 *
 * Features:
 * - Clear visual feedback of current state
 * - Informative tooltip explaining the setting's purpose
 * - Prevents deselection of current value
 *
 * @example
 * ```tsx
 * <LocationTransientToggle
 *   isTransient={location.isTransient}
 *   onChange={handleTransientChange}
 * />
 * ```
 */
export function LocationTransientToggle(props: Props) {
  // Convert boolean to string value for toggle group
  const value = props.isTransient ? 'transient' : 'non-transient';

  /**
   * Handles changes to the toggle button group.
   * Converts the string value back to boolean and calls onChange.
   */
  const handleChangeTransientToggle = (
    _: unknown,
    value: 'transient' | 'non-transient'
  ) => {
    const isTransient = value === 'transient';
    props.onChange(isTransient);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      paddingX={1}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2">Transient location</Typography>
        <Tooltip
          arrow
          placement="right"
          title="Transient locations are used when assets make only occasional or temporary stops—these locations are hidden from the location list by default"
        >
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </Stack>
      <Paper variant="outlined">
        <TransientButtonGroup
          exclusive
          fullWidth
          size="small"
          value={value}
          onChange={handleChangeTransientToggle}
        >
          <ToggleButton value="transient">Yes</ToggleButton>
          <ToggleButton value="non-transient">No</ToggleButton>
        </TransientButtonGroup>
      </Paper>
    </Stack>
  );
}
